import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Receipt, Users, Calendar, AlertTriangle, CheckCircle, Eye,
} from 'lucide-react';
import { DebouncedInput } from '../shared/DebouncedInputs';

/**
 * Tab "Facturación": proyección de facturación a futuro con cuotas agrupadas por mes.
 */
const FacturacionTab = ({
  proyeccionData,
  busquedaProyeccion, setBusquedaProyeccion,
  loadingProyeccion,
  cuotasProyeccionFiltradas,
  cuotasAgrupadasPorMes,
  fetchProyeccionData,
  cuotaSeleccionada, setCuotaSeleccionada,
  abrirModalFacturar,
  handleViewCPS,
  formatRutSinPuntos,
}) => (
  <div className='space-y-6'>
    <div className='flex items-center justify-between'>
      <div>
        <h3 className='text-xl font-semibold text-slate-900 flex items-center gap-2'>
          <Receipt className='w-5 h-5 text-indigo-600' />
          Proyección de Facturación a Futuro
        </h3>
        <p className='text-sm text-slate-500'>
          Horizonte de cobros próximos 12 meses • {proyeccionData.total_cuotas || 0} cuotas programadas
        </p>
      </div>
      <div className='flex items-center gap-3'>
        <div className='relative'>
          <DebouncedInput
            type='text'
            placeholder='Buscar por RUT, Cliente o PSP...'
            value={busquedaProyeccion}
            onChange={(e) => setBusquedaProyeccion(e.target.value)}
            className='w-64 pl-8 h-9 text-sm'
            data-testid='busqueda-proyeccion'
          />
          <Users className='w-4 h-4 text-slate-400 absolute left-2.5 top-2.5' />
        </div>
      </div>
    </div>

    <Card className='p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200'>
      <div className='flex justify-between items-center'>
        <div className='flex gap-6'>
          <div className='flex items-center gap-2'>
            <div className='w-3 h-3 rounded-full bg-orange-400'></div>
            <span className='text-sm text-slate-600'>Pendiente: <strong className='text-orange-600'>{proyeccionData.totales?.total_pendiente_uf?.toFixed(2) || 0} UF</strong></span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-3 h-3 rounded-full bg-blue-400'></div>
            <span className='text-sm text-slate-600'>Facturado: <strong className='text-blue-600'>{proyeccionData.totales?.total_facturado_uf?.toFixed(2) || 0} UF</strong></span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-3 h-3 rounded-full bg-indigo-500'></div>
            <span className='text-sm text-slate-600'>Total: <strong className='text-indigo-700'>{proyeccionData.totales?.total_general_uf?.toFixed(2) || 0} UF</strong></span>
          </div>
        </div>
        <Button size='sm' variant='outline' onClick={fetchProyeccionData} className='text-xs'>
          Actualizar
        </Button>
      </div>
    </Card>

    <Card className='border-slate-200' data-testid='proyeccion-facturacion-futuro'>
      <div className='max-h-[550px] overflow-y-auto' style={{ scrollbarWidth: 'thin' }}>
        {loadingProyeccion ? (
          <div className='p-12 text-center'>
            <div className='animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2'></div>
            <p className='text-slate-600'>Cargando proyección...</p>
          </div>
        ) : cuotasProyeccionFiltradas.length === 0 ? (
          <div className='p-12 text-center'>
            <Calendar className='w-12 h-12 text-slate-300 mx-auto mb-3' />
            <p className='text-slate-600 font-medium'>No hay cuotas programadas</p>
            <p className='text-sm text-slate-400'>Las cuotas de PSPs aceptadas aparecerán aquí para facturación</p>
          </div>
        ) : (
          <div>
            {cuotasAgrupadasPorMes.map((grupo) => (
              <div key={grupo.mesKey} className='border-b border-slate-200 last:border-b-0'>
                <div className='bg-gradient-to-r from-indigo-100 to-purple-100 px-4 py-2 flex items-center justify-between sticky top-0 z-5'>
                  <div className='flex items-center gap-2'>
                    <Calendar className='w-4 h-4 text-indigo-600' />
                    <span className='font-semibold text-indigo-800'>{grupo.mesNombre}</span>
                    <Badge variant='outline' className='text-xs bg-white/70'>
                      {grupo.cuotas.length} {grupo.cuotas.length === 1 ? 'cuota' : 'cuotas'}
                    </Badge>
                  </div>
                  <span className='font-bold text-indigo-700'>{grupo.totalUF.toFixed(2)} UF</span>
                </div>

                <table className='w-full text-sm'>
                  <thead className='bg-slate-50'>
                    <tr>
                      <th className='text-left p-2 pl-4 font-medium text-slate-500 text-xs'>Fecha</th>
                      <th className='text-left p-2 font-medium text-slate-500 text-xs'>Cliente / PSP</th>
                      <th className='text-right p-2 font-medium text-slate-500 text-xs'>Monto</th>
                      <th className='text-center p-2 font-medium text-slate-500 text-xs'>Área</th>
                      <th className='text-center p-2 font-medium text-slate-500 text-xs'>Estado</th>
                      <th className='text-center p-2 pr-4 font-medium text-slate-500 text-xs'>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupo.cuotas.map((cuota, idx) => (
                      <tr
                        key={`${cuota.proposal?.id}-${cuota.cuota?.index}-${idx}`}
                        className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors
                          ${cuota.es_vencida ? 'bg-red-50/50' : ''}
                          ${cuotaSeleccionada?.proposal?.id === cuota.proposal?.id && cuotaSeleccionada?.cuota?.index === cuota.cuota?.index ? 'bg-indigo-50' : ''}
                        `}
                        onClick={() => setCuotaSeleccionada(cuota)}
                      >
                        <td className='p-2 pl-4'>
                          <div className='flex items-center gap-2'>
                            {cuota.es_vencida && <AlertTriangle className='w-3.5 h-3.5 text-red-500' />}
                            <div>
                              <p className={`font-medium text-sm ${cuota.es_vencida ? 'text-red-700' : 'text-slate-900'}`}>
                                {cuota.fecha_emision_formatted}
                              </p>
                              {cuota.dias_restantes !== null && (
                                <p className='text-[10px] text-slate-500'>
                                  {cuota.es_vencida ? 'Vencida' : `En ${cuota.dias_restantes} días`}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className='p-2'>
                          <p className='font-medium text-slate-900 text-sm'>{cuota.cliente?.company || cuota.cliente?.name || 'N/A'}</p>
                          <p className='text-[10px] text-slate-500 font-mono'>{cuota.proposal?.folio}</p>
                          <p className='text-[10px] text-indigo-600 font-medium'>
                            Cuota {cuota.cuota?.numero || 1} de {cuota.proposal?.total_cuotas || '?'}
                          </p>
                        </td>
                        <td className='p-2 text-right'>
                          <p className='font-bold text-slate-900'>{cuota.cuota?.monto_uf} UF</p>
                          <p className='text-[10px] text-slate-500'>
                            ${new Intl.NumberFormat('es-CL').format(cuota.cuota?.monto_clp || 0)}
                          </p>
                        </td>
                        <td className='p-2 text-center'>
                          <Badge variant='outline' className='text-[10px]'>
                            {cuota.area}
                          </Badge>
                        </td>
                        <td className='p-2 text-center'>
                          <Badge
                            variant='outline'
                            className={
                              cuota.cuota?.status === 'Pagada' ? 'bg-green-50 text-green-700 border-green-200 text-[10px]' :
                              cuota.cuota?.status === 'Pago Parcial' ? 'bg-purple-50 text-purple-700 border-purple-200 text-[10px]' :
                              cuota.cuota?.status === 'Facturada' ? (
                                cuota.cuota?.factura_info?.es_vencida
                                  ? 'bg-red-50 text-red-700 border-red-200 text-[10px]'
                                  : 'bg-blue-50 text-blue-700 border-blue-200 text-[10px]'
                              ) :
                              'bg-orange-50 text-orange-700 border-orange-200 text-[10px]'
                            }
                          >
                            {cuota.cuota?.es_cuota_exito ? '🏆 Éxito' : (
                              cuota.cuota?.status === 'Facturada' ? (
                                cuota.cuota?.factura_info?.es_vencida
                                  ? `Vencida ${cuota.cuota.factura_info.dias_vencida}d`
                                  : 'Pago pend.'
                              ) : cuota.cuota?.status
                            )}
                          </Badge>
                        </td>
                        <td className='p-2 pr-4 text-center'>
                          {cuota.cuota?.status === 'Por Facturar' && (
                            <Button
                              size='sm' variant='outline'
                              className='h-6 text-[10px] bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200'
                              onClick={(e) => { e.stopPropagation(); abrirModalFacturar(cuota); }}
                            >
                              Facturar
                            </Button>
                          )}
                          {(cuota.cuota?.status === 'Facturada' || cuota.cuota?.status === 'Pago Parcial') && (
                            <span className='text-[10px] text-blue-600 flex items-center justify-center gap-1'>
                              <CheckCircle className='w-3 h-3' />
                              {cuota.cuota?.status === 'Pago Parcial' ? 'Pago Parcial' : 'En Cobranza'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {cuotaSeleccionada && (
        <div className='p-4 border-t border-slate-200 bg-slate-50'>
          <div className='flex justify-between items-start'>
            <div className='flex-1'>
              <div className='flex items-center gap-3 mb-2'>
                <h5 className='font-semibold text-slate-900'>Detalle de Cuota Seleccionada</h5>
                <Badge variant='outline' className='text-xs bg-indigo-50 text-indigo-700'>
                  Cuota #{cuotaSeleccionada.cuota?.numero}
                </Badge>
              </div>

              <div className='grid grid-cols-3 gap-4'>
                <div className='bg-white p-3 rounded-lg border border-slate-200'>
                  <p className='text-xs text-slate-500 font-semibold mb-1'>DATOS DE FACTURACIÓN</p>
                  <p className='text-sm font-medium text-slate-900'>{cuotaSeleccionada.facturacion?.nombre_razon_social}</p>
                  <p className='text-sm font-mono text-slate-600'>RUT: {formatRutSinPuntos(cuotaSeleccionada.facturacion?.rut)}</p>
                </div>

                <div className='bg-white p-3 rounded-lg border border-slate-200'>
                  <p className='text-xs text-slate-500 font-semibold mb-1'>PROPUESTA</p>
                  <p className='text-sm font-medium text-slate-900'>{cuotaSeleccionada.proposal?.titulo}</p>
                  <p className='text-sm font-mono text-primary'>{cuotaSeleccionada.proposal?.folio}</p>
                </div>

                <div className='bg-white p-3 rounded-lg border border-slate-200'>
                  <p className='text-xs text-slate-500 font-semibold mb-1'>VERIFICAR CLÁUSULAS</p>
                  <Button
                    size='sm' variant='outline'
                    className='w-full text-xs mt-1'
                    onClick={() => handleViewCPS(cuotaSeleccionada.proposal?.id, cuotaSeleccionada.proposal?.folio)}
                  >
                    <Eye className='w-3.5 h-3.5 mr-1' />
                    Ver CPS
                  </Button>
                </div>
              </div>
            </div>

            <Button
              variant='ghost' size='sm'
              onClick={() => setCuotaSeleccionada(null)}
              className='ml-4'
            >
              ✕
            </Button>
          </div>
        </div>
      )}
    </Card>
  </div>
);

export default FacturacionTab;
